import { countTokens } from 'token-estimator';

/**
 * Represents a chunk of text for processing
 */
export interface TextChunk {
  text: string; // Unwrapped text for this chunk
  chunkIndex: number; // 0-based chunk index
  sourceStartLine: number; // 1-based source line where chunk starts (in original text)
  sourceEndLine: number; // 1-based source line where chunk ends (exclusive)
  globalWrappedLineOffset: number; // 0-based offset to add to chunk-relative wrapped line numbers
  totalWrappedLines: number; // Total wrapped lines in this chunk
  tokenCount: number; // Actual token count of this chunk
}

/**
 * Format text with line numbers, wrapping at column 100
 * This must match the backend implementation exactly
 */
export function formatTextWithLineNumbers(text: string): string {
  const lines = text.split('\n');
  const formattedLines: string[] = [];
  let currentLineNumber = 1;

  for (const line of lines) {
    if (line.length <= 100) {
      // Line fits within limit
      formattedLines.push(`${currentLineNumber.toString().padStart(4, ' ')} | ${line}`);
      currentLineNumber++;
    } else {
      // Need to wrap line
      let remaining = line;
      while (remaining.length > 0) {
        const chunk = remaining.substring(0, 100);
        formattedLines.push(`${currentLineNumber.toString().padStart(4, ' ')} | ${chunk}`);
        remaining = remaining.substring(100);
        currentLineNumber++;
      }
    }
  }

  return formattedLines.join('\n');
}

/**
 * Extract wrapped lines by line number range (inclusive)
 * Returns the actual content without line number prefixes
 */
export function extractWrappedLines(text: string, startLine: number, endLine: number): string {
  const wrapped = formatTextWithLineNumbers(text);
  const wrappedLines = wrapped.split('\n');

  // Extract lines (1-indexed, inclusive)
  const selectedLines = wrappedLines.slice(startLine - 1, endLine);

  // Remove line number prefixes (format: "   1 | content")
  const contentLines = selectedLines.map(line => {
    const pipeIndex = line.indexOf('|');
    return pipeIndex >= 0 ? line.substring(pipeIndex + 2) : line;
  });

  return contentLines.join('\n');
}

/**
 * Get total number of wrapped lines
 */
export function getTotalWrappedLines(text: string): number {
  const lines = text.split('\n');
  let total = 0;

  for (const line of lines) {
    if (line.length <= 100) {
      total++;
    } else {
      total += Math.ceil(line.length / 100);
    }
  }

  return total;
}

/**
 * Split text into chunks based on token count with overlap between chunks
 * @param text - The text to chunk
 * @param maxTokensPerChunk - Maximum tokens per chunk (default: 20000)
 * @param overlapSourceLines - Number of source lines to overlap between chunks (default: 10)
 * @returns Array of text chunks with metadata
 */
export function chunkText(
  text: string,
  maxTokensPerChunk: number = 20000,
  overlapSourceLines: number = 10
): TextChunk[] {
  const sourceLines = text.split('\n');
  const chunks: TextChunk[] = [];
  let currentSourceLineIndex = 0;

  while (currentSourceLineIndex < sourceLines.length) {
    // Build chunk starting from currentSourceLineIndex
    let chunkEndIndex = currentSourceLineIndex;
    let chunkLines: string[] = [];
    let chunkTokenCount = 0;

    // Add lines until we hit token limit
    while (chunkEndIndex < sourceLines.length) {
      const line = sourceLines[chunkEndIndex];
      const lineTokens = countTokens(line + '\n');

      // Check if adding this line would exceed limit
      if (chunkTokenCount + lineTokens > maxTokensPerChunk && chunkLines.length > 0) {
        break;
      }

      chunkLines.push(line);
      chunkTokenCount += lineTokens;
      chunkEndIndex++;
    }

    // Force at least one line to avoid infinite loop
    if (chunkLines.length === 0 && chunkEndIndex < sourceLines.length) {
      chunkLines.push(sourceLines[chunkEndIndex]);
      chunkTokenCount = countTokens(sourceLines[chunkEndIndex] + '\n');
      chunkEndIndex++;
    }

    const chunkText = chunkLines.join('\n');

    // Calculate global wrapped line offset
    // This is the number of wrapped lines in all source lines BEFORE this chunk's start
    const textBeforeChunk = currentSourceLineIndex === 0 ? '' : sourceLines.slice(0, currentSourceLineIndex).join('\n');
    const wrappedLinesBeforeChunk = currentSourceLineIndex === 0 ? 0 : getTotalWrappedLines(textBeforeChunk);

    chunks.push({
      text: chunkText,
      chunkIndex: chunks.length,
      sourceStartLine: currentSourceLineIndex + 1, // 1-based
      sourceEndLine: chunkEndIndex, // 1-based, exclusive
      globalWrappedLineOffset: wrappedLinesBeforeChunk,
      totalWrappedLines: getTotalWrappedLines(chunkText),
      tokenCount: chunkTokenCount
    });

    // Move to next chunk with overlap
    const nextStart = chunkEndIndex - overlapSourceLines;
    currentSourceLineIndex = Math.max(nextStart, currentSourceLineIndex + 1);

    // If we've processed all lines, break
    if (chunkEndIndex >= sourceLines.length) {
      break;
    }
  }

  return chunks;
}
